/**
 * Telegram Mini Apps SDK bootstrap.
 *
 * Only real @telegram-apps/sdk v3 exports are used here - nothing
 * invented. See https://docs.telegram-mini-apps.com for the reference.
 */
import {
  init as initSdk,
  retrieveRawInitData,
  mountMiniApp,
  mountViewport,
  mountThemeParams,
  mountBackButton,
  bindViewportCssVars,
  bindThemeParamsCssVars,
  expandViewport,
  backButton,
  openTelegramLink,
  openLink,
} from "@telegram-apps/sdk";

let cleanup: VoidFunction | null = null;

function safeCall(label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    // Any of these can throw when the page isn't actually running inside
    // a Telegram client (plain browser tab, wrong domain, etc.), or on
    // platforms that don't support a given component. None of that
    // should ever prevent the React app itself from rendering - a
    // blank white page is a much worse failure mode than a Mini App
    // that just runs with degraded Telegram integration.
    console.warn(`[telegram] ${label} failed, continuing without it`, err);
  }
}

export function bootstrapTelegram(): void {
  if (cleanup) return; // already initialized

  safeCall("init", () => {
    cleanup = initSdk();
  });
  safeCall("mountMiniApp", mountMiniApp);
  safeCall("mountThemeParams", mountThemeParams);
  safeCall("bindThemeParamsCssVars", bindThemeParamsCssVars);

  try {
    mountViewport()
      .then(() => {
        safeCall("bindViewportCssVars", bindViewportCssVars);
        safeCall("expandViewport", expandViewport);
      })
      .catch((err) => {
        // Not fatal - the app still works at whatever viewport size the
        // WebView already gave us, just without safe-area CSS vars.
        console.warn("[telegram] viewport mount failed", err);
      });
  } catch (err) {
    console.warn("[telegram] mountViewport threw synchronously", err);
  }

  safeCall("mountBackButton", mountBackButton);

  // DARK_THEME_TELEGRAM: شريط تيليجرام العلوي/السفلي والخلفية بنفس لون التطبيق
  safeCall("darkChrome", () => {
    const wa = (window as any)?.Telegram?.WebApp;
    if (!wa) return;
    const bg = "#0e1621";
    try { wa.setHeaderColor?.(bg); } catch {}
    try { wa.setBackgroundColor?.(bg); } catch {}
    try { wa.setBottomBarColor?.(bg); } catch {}
  });
}

export function showBackButton(onClick: () => void): VoidFunction {
  try {
    backButton.show();
    return backButton.onClick(onClick);
  } catch (err) {
    console.warn("[telegram] showBackButton failed", err);
    return () => undefined;
  }
}

export function hideBackButton(): void {
  try {
    backButton.hide();
  } catch (err) {
    console.warn("[telegram] hideBackButton failed", err);
  }
}

/**
 * Raw initData string, sent verbatim as `Authorization: tma <value>` on
 * every backend request. The backend re-derives and verifies the HMAC
 * signature itself - the frontend must never try to "parse the user out"
 * and send that instead, since that value on its own carries no proof of
 * authenticity.
 */
export function getRawInitData(): string {
  try {
    const fromSdk = retrieveRawInitData();
    if (fromSdk) return fromSdk;
  } catch {
    // Falls through to the native fallback below instead of returning
    // empty immediately - some launch contexts (e.g. the Main Mini App,
    // opened from the bot's profile or via a ?startapp link) have been
    // seen not being picked up by this SDK's own retrieval logic, even
    // though Telegram's own telegram-web-app.js still exposes the data
    // directly on window.Telegram.WebApp.
  }

  try {
    const native = (window as any)?.Telegram?.WebApp?.initData;
    if (typeof native === "string" && native) return native;
  } catch {
    // ignore - window.Telegram not present at all (plain browser, etc.)
  }

  // Last resort: read tgWebAppData straight out of the URL itself
  // (hash fragment, with a query-string fallback for clients that put
  // it there instead). This is the one source that's guaranteed to be
  // present the INSTANT the page loads - unlike the SDK or
  // window.Telegram.WebApp.initData above, it can't be affected by the
  // Telegram JS bridge still being mid-initialization when this code
  // runs (a known timing gap, worse on some launch contexts like the
  // Main Mini App than others).
  try {
    for (const raw of [window.location.hash.replace(/^#/, ""), window.location.search.replace(/^\?/, "")]) {
      const params = new URLSearchParams(raw);
      const fromUrl = params.get("tgWebAppData");
      if (fromUrl) return fromUrl;
    }
  } catch {
    // ignore - malformed URL, shouldn't normally happen
  }

  // Running outside Telegram (e.g. `npm run dev` in a plain browser), or
  // none of the three sources had data. Return an empty string; the
  // backend will reject requests with a clear 401 instead of the app
  // silently pretending to be logged in.
  return "";
}

/**
 * Opens a t.me link (e.g. the sticker pack's addstickers link) the
 * proper Mini-App way. Inside Telegram this closes the Mini App and
 * hands off to Telegram's native "add stickers" screen. Outside
 * Telegram (plain browser testing) it just opens a normal new tab.
 */
export function openPackLink(url: string): void {
  try {
    openTelegramLink(url);
  } catch (err) {
    console.warn("[telegram] openTelegramLink unavailable, falling back to window.open", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Opens any external URL (a banner's target, the YouTube tutorial, an
 * update channel that might not even be a t.me link) via the SDK's
 * general-purpose link opener - openTelegramLink above is specifically
 * for t.me/tg:// deep links and isn't the right tool for an arbitrary
 * external URL.
 */
export function openExternalLink(url: string): void {
  try {
    openLink(url);
  } catch (err) {
    console.warn("[telegram] openLink unavailable, falling back to window.open", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}